import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";

function toLocalInput(date) {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const NO_PATIENT = "__no_patient__";

export default function AppointmentDialog({ open, onClose, onSaved, patients = [], editing, defaultStart }) {
  const [form, setForm] = useState({
    title: "",
    patient_id: "",
    start: "",
    end: "",
    description: "",
    location: "",
    reminder_phone: "",
    sync_to_google: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title || "",
        patient_id: editing.patient_id || "",
        start: toLocalInput(editing.start),
        end: toLocalInput(editing.end),
        description: editing.description || "",
        location: editing.location || "",
        reminder_phone: editing.reminder_phone || "",
        sync_to_google: true,
      });
    } else {
      const start = defaultStart || new Date();
      const end = new Date(start);
      end.setHours(end.getHours() + 1);
      setForm({
        title: "",
        patient_id: "",
        start: toLocalInput(start),
        end: toLocalInput(end),
        description: "",
        location: "",
        reminder_phone: "",
        sync_to_google: true,
      });
    }
  }, [open, editing, defaultStart]);

  const handlePatientChange = (val) => {
    if (val === NO_PATIENT) {
      setForm((f) => ({ ...f, patient_id: "" }));
      return;
    }
    const patient = patients.find((p) => p.id === val);
    setForm((f) => ({
      ...f,
      patient_id: val,
      reminder_phone: f.reminder_phone || (patient?.phone || ""),
      title: f.title || `Consulta - ${patient?.name || ""}`,
    }));
  };

  const handleSubmit = async () => {
    if (!form.title || !form.start || !form.end) {
      toast.error("Completa título, inicio y fin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        patient_id: form.patient_id || null,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
      };
      if (editing) {
        await api.put(`/appointments/${editing.id}`, payload);
        toast.success("Cita actualizada");
      } else {
        await api.post("/appointments", payload);
        toast.success("Cita creada");
      }
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-[400] text-[#3A2418]">
            {editing ? "Editar cita" : "Nueva cita"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-sm">Título</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Consulta general"
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="appt-title-input"
            />
          </div>

          <div>
            <Label className="text-sm">Paciente</Label>
            <Select value={form.patient_id || NO_PATIENT} onValueChange={handlePatientChange}>
              <SelectTrigger className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="appt-patient-select">
                <SelectValue placeholder="Sin paciente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PATIENT}>Sin paciente</SelectItem>
                {patients.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Inicio</Label>
              <Input
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                className="mt-1.5 bg-white border-[#E8DFD2]"
                data-testid="appt-start-input"
              />
            </div>
            <div>
              <Label className="text-sm">Fin</Label>
              <Input
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                className="mt-1.5 bg-white border-[#E8DFD2]"
                data-testid="appt-end-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm">Ubicación</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Consultorio, dirección o videollamada"
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="appt-location-input"
            />
          </div>

          <div>
            <Label className="text-sm">Teléfono recordatorio (WhatsApp)</Label>
            <Input
              value={form.reminder_phone}
              onChange={(e) => setForm({ ...form, reminder_phone: e.target.value })}
              placeholder="+52 55 1234 5678"
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="appt-phone-input"
            />
            <p className="text-xs text-[#6F5A4C] mt-1">Se autocompletará con el del paciente si lo seleccionas.</p>
          </div>

          <div>
            <Label className="text-sm">Notas</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="appt-description-input"
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-md bg-[#F4EFE4]">
            <div>
              <div className="text-sm font-medium text-[#3A2418]">Sincronizar con Google Calendar</div>
              <div className="text-xs text-[#6F5A4C]">Crea el evento también en tu calendario primario</div>
            </div>
            <Switch
              checked={form.sync_to_google}
              onCheckedChange={(v) => setForm({ ...form, sync_to_google: v })}
              data-testid="appt-sync-switch"
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="appt-save-btn">
            {saving ? "Guardando..." : editing ? "Actualizar" : "Crear cita"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
